import { Button, Grid, Theme, Box, useTheme, Alert, AlertTitle, Tooltip } from "@mui/material";
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAssociatedFlights } from "src/redux/actions/ContentManagement/ContentManagement.Actions";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import {
  ContentType,
  IContent,
  IContentAssociatedFlight,
  IContentType,
  IFlight,
  IFullFlight,
} from "@vmosolutions/oc-interfaces";
import { Footer } from "src/components/ContentManagement/Footer";
import { getFlightById } from "src/redux/actions/flights/FlightData.Actions";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useDispatch } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { FileDrop } from "./FileDrop";
import { FlightDropTarget } from "./FlightDropTarget";
// Yup Validation
import AddBox from "@mui/icons-material/AddBox";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { openWindow, updateTabNodeByWindowIdAndProps, removeTabNode } from "src/redux/actions/Mosaic.Actions";
import * as Yup from "yup";
import _ from "lodash";
import { WarningModal } from "src/components/ModalComponents/WarningModal";

interface IContentFile {
  file: File;
  fileData: ArrayBuffer;
}

export interface IManageContentProps {
  id?: string;
  path?: string[];
  windowProps: {
    contentId?: string;
    initialSelectedFlightId?: string;
    injectedFlights?: IFullFlight[]; // If opened from the flight editor, provide flight info.
    setLoadedEvent?: any;
  };
  setCloseWarning: () => void;
}

interface ICreateContentResponse {
  content: IContent;
  uploadURL: string;
}

const defaultInitialContent: IContent = {
  _id: undefined,
  contentType: undefined,
  fileName: undefined,
  uploadedBy: undefined,
  fileType: undefined,
  fileSize: undefined,
  submittedByIP: undefined,
  fields: {},
  source: undefined,
  associatedFlights: [],
};

const ManageContentStyles = (theme: Theme): any => ({
  display: "flex",
  flexGrow: 1,
  margin: "5px",
  "& h1": {
    margin: 0,
  },
  "& .addContentTitle": {
    fontSize: 35,
    color: "white",
  },
  "& .content_root": {
    margin: "10px",
    color: theme.palette.primary.contrastText,
    fontSize: "1.5 rem",
    fontWeight: 400,
    fontFamily: "inherit",
    "& .select": {
      color: theme.palette.common.black,
      margin: "10px 0px",
    },
    "& .add_new": {
      minWidth: 20,
      "& svg": {
        width: 16,
        height: 16,
        margin: "5px",
      },
      padding: "2px 8px",
      margin: "0px 5px",
      fontSize: 12,
    },
    "& .buttons": {
      marginTop: "10px",
      display: "flex",
      justifyContent: "flex-start",
      "& .btn": {
        flexWrap: "wrap",
        display: "flex",
        height: 27,
        fontSize: 10,
        lineHeight: "12px",
        padding: "2px 8px",
        margin: "0px 4px",
      },
    },
    "& .error-message": {
      maxWidth: 300,
      padding: "0px 5px",
      fontWeight: 300,
      color: theme.palette.error.main,
      fontSize: "0.75rem",
    },
  },
});

//   if (content?.associatedFlights?.length > 0) {
export const getInitialValues = async (
  contentId?: string,
  flightIds?: string[],
  dispatch?: Dispatch<any>
): Promise<IContent> => {
  let content: IContent = { ...defaultInitialContent };
  const notifications: INotifications = {
    dispatch: dispatch,
    infoMessage: "",
    successMessage: "",
    errorMessage: "Error: Unable to fetch content",
  };

  if (contentId) {
    const response = await AmplifyAPIWrapper.Get<IContent>(`/content/${contentId}`, notifications);
    if (response?._id) {
      content = response;
    }
  }
  if (content?.associatedFlights?.length > 0) {
    const data: string[] = [];
    content?.associatedFlights.forEach((each: IContentAssociatedFlight | string) => {
      return typeof each !== "string" ? data.push(each?._id) : data.push(each);
    });
    return {
      ...content,
      associatedFlights: (await getAssociatedFlights(data)).map((flight) => flight),
    };
  }
  //maybe it was just an empty array on the content
  return {
    ...content,
    associatedFlights: flightIds?.length > 0 ? (await getAssociatedFlights(flightIds)).map((flight) => flight) : [],
  };
};

// Create formik form type.
export type IContentForm = FormikProps<IContent>;

export const ManageContentWindow = (props: IManageContentProps): JSX.Element => {
  const { windowProps, id, path, setCloseWarning } = props;
  const { contentId, initialSelectedFlightId, injectedFlights, setLoadedEvent } = windowProps;

  const dispatch = useDispatch();

  const theme = useTheme();

  const fileUploadRef = useRef<any>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const [contentFile, setContentFile] = useState<IContentFile | null>(null);
  const [contentType, setContentType] = useState<IContentType>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [content, setContent] = useState<IContent>(defaultInitialContent);

  const customSchema: string = useMemo(() => contentType?.fields ?? "", [contentType]);
  const schema = useMemo(() => {
    try {
      const data = customSchema ? JSON.parse(customSchema) : null;
      return data;
    } catch (error) {
      return null;
    }
  }, [customSchema]);

  // Default Content Form Validation with YUP
  const defaultValidateSchema = Yup.object().shape({
    contentType: Yup.object().required("Content Type is required").nullable(),
  });

  const [contentTypeOptions, setContentTypeOptions] = useState<IContentType[]>([]);
  const [contentTypeCreateOptions, setContentTypeCreateOptions] = useState<IContentType[]>([]);
  const [loadingContentTypes, setLoadingContentTypes] = useState<boolean>(false);

  useEffect(() => {
    const getContentTypeAction = async (): Promise<void> => {
      setLoadingContentTypes(true);
      const response = await AmplifyAPIWrapper.Get<IContentType[]>("/contentTypes");
      if (Array.isArray(response)) {
        setContentTypeOptions(response);
        setContentTypeCreateOptions(response.filter((content: IContentType) => !content.systemGeneratedOnly));
      }
      setLoadingContentTypes(false);
    };
    if (!contentTypeOptions.length) getContentTypeAction();
  }, [contentTypeOptions.length]);

  const [initialValues, setInitialValues] = useState<IContent>({ ...defaultInitialContent });
  const [cancelLoad, setCancelLoad] = useState<boolean>(false);

  const customPath = "fields";
  const initialCustomValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    if (!schema || !schema.hasOwnProperty("properties") || !contentType) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] = schema.properties[value].default ?? schema.properties[value]?.selectorProps?.defaultValue;
    });
    return values;
  }, [schema, contentType]);

  // If initialCustomValues exist, update initial values with custom defaults and contentType
  useEffect(() => {
    if (Object.keys(initialCustomValues).length) {
      if (initialValues?._id) {
        setInitialValues((prev) => _.merge({ [customPath]: initialCustomValues, contentType }, prev));
      } else {
        setInitialValues((prev) => _.merge(prev, { [customPath]: initialCustomValues, contentType }));
      }
    }
  }, [initialCustomValues, contentType, initialValues?._id]);

  // Need to do this for the attached files etc...
  //maybe have a getcontent func outside of this componet that does all that.
  const loadContent = useCallback(
    async (contentId: string): Promise<void> => {
      setCancelLoad(false);
      setIsLoading(true);
      const values = await getInitialValues(contentId, [initialSelectedFlightId], dispatch);
      if (!cancelLoad) {
        setContent(values);
        setInitialValues(values);
        setContentType(values.contentType);
      }
      setIsLoading(false);
    },
    [dispatch, cancelLoad, initialSelectedFlightId]
  );

  // If there's a contentID and flight id supplied load content.
  useEffect(() => {
    if (contentId) {
      loadContent(contentId);
    }
    return () => {
      setCancelLoad(true);
    };
  }, [contentId, loadContent]);

  // If there's fullflight data provided from the window props, it's been opened from the editor, append flight to values.
  useEffect(() => {
    if (injectedFlights?.length > 0) {
      const updatedValues = { ...defaultInitialContent, associatedFlights: injectedFlights };
      setInitialValues(updatedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (content) {
      setUploading(false);
    }
  }, [content]);

  const handleSubmit = async (values: IContent, helpers: FormikHelpers<IContent>): Promise<void> => {
    const { setSubmitting, setStatus } = helpers;
    const mode = initialValues?._id ? "update" : "create";

    setSubmitting(true);
    setUploading(true);
    //change to an array of flight to just ids
    values.associatedFlights = (values.associatedFlights as IFlight[]).map((flight: any) => flight?._id);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Content...`,
    };

    if (mode === "create") {
      const response = await AmplifyAPIWrapper.Post<ICreateContentResponse>("/content", values, notifications);
      if (contentFile?.fileData && response?.uploadURL) {
        //I think this is putting the file in s3 at the address the backend returned?
        // const uploadResponse =
        await fetch(response?.uploadURL, {
          method: "PUT",
          body: contentFile?.fileData,
        });
      }
      if (response?.content?._id) {
        setStatus({ success: true });
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "Content Successfully Created",
          })
        );
        //Update the mosaic window title
        dispatch(
          updateTabNodeByWindowIdAndProps(
            id,
            {
              title: "Edit Content",
              props: {
                contentId: response?.content?._id, // pass in the contentId to force retrieval of content and set manage window in edit mode.
                initialSelectedFlightId: initialSelectedFlightId,
                setLoadedEvent: setLoadedEvent,
              },
            },
            windowProps,
            "ManageContentWindow"
          )
        );
        if (initialSelectedFlightId) {
          const flight = await getFlightById(initialSelectedFlightId);
          if (flight?._id && setLoadedEvent) setLoadedEvent(flight);
        }
      } else {
        setStatus({ success: false });
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: null,
            variant: "error",
            message: "Error: Unable to Create Content",
          })
        );
      }
    } else {
      const response = await AmplifyAPIWrapper.Put<any>(`/content/${values?._id}`, values, notifications);
      if (response?.content?._id) {
        const updatedValues = await getInitialValues(response.content._id, undefined, dispatch);
        setInitialValues(updatedValues);
        setContent(updatedValues);
        setStatus({ success: true });

        if (initialSelectedFlightId) {
          const flight = await getFlightById(initialSelectedFlightId);
          if (flight?._id && setLoadedEvent) setLoadedEvent(flight);
        }
      } else {
        setStatus({ success: false });
      }
    }
    setSubmitting(false);
  };

  const handleDeleteContent = async (formProps: IContentForm): Promise<void> => {
    const { setSubmitting, values, resetForm, setStatus } = formProps;
    setSubmitting(true);
    setUploading(true);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Removing Content...",
      successMessage: "Content Successfully Removed.",
      errorMessage: "Error: Unable to Remove Content",
    };
    const response = await AmplifyAPIWrapper.Del<any>(`/content/${values?._id}`, null, notifications);

    if (response?.content?._id) {
      if (initialSelectedFlightId) {
        const flight = await getFlightById(initialSelectedFlightId);
        if (flight?._id && setLoadedEvent) setLoadedEvent(flight);
      }
      setContent({ ...defaultInitialContent });
      resetForm({ values: { ...defaultInitialContent } });
      setContentType(null);
      setStatus({ success: true });
      if (path) dispatch(removeTabNode(path, id));
    }
    if (path) dispatch(removeTabNode(path, id));
    setSubmitting(false);
    setUploading(false);
  };

  const handleAddNewContent = useCallback(
    (formProps: IContentForm): void => {
      const newContentFlights =
        injectedFlights?.length > 0
          ? injectedFlights
          : formProps?.values?.associatedFlights?.length
            ? formProps?.values?.associatedFlights
            : [];

      dispatch(
        openWindow(
          "Create Content",
          "ManageContentWindow",
          [],
          {
            initialSelectedFlightId: initialSelectedFlightId,
            injectedFlights: newContentFlights,
          },
          "context"
        )
      );
    },
    [dispatch, initialSelectedFlightId, injectedFlights]
  );

  const openReviewContentWindow = (): void => {
    dispatch(openWindow("Review Content", "ReviewContentWindow", [], { content: initialValues }, "content"));
  };

  if (!loadingContentTypes) {
    return (
      <Box sx={ManageContentStyles(theme)}>
        <Grid id="contentRoot" container direction="column" className="content_root">
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            validationSchema={defaultValidateSchema}
          >
            {(formProps: IContentForm) => {
              const { values, dirty, isValid, isSubmitting, handleReset, errors } = formProps;
              const contentTypeAssigned = !!formProps?.values?.contentType;

              const handleResetContentForm = (): void => {
                // Removes any files added to the file upload tool before resetting to it's initial values.
                if (fileUploadRef?.current?.hasOwnProperty("removeAllFiles")) {
                  fileUploadRef.current.removeAllFiles();
                }
                setTimeout(() => handleReset(), 0);
              };

              const getDeleteTooltip = (): string => {
                if (values?.contentType?.allowDelete === false) {
                  return "This content type cannot be deleted.";
                }
                if (values?.associatedFlights?.length > 0) {
                  return "Content with associated flights cannot be deleted.";
                }
                return "";
              };

              return (
                <Form>
                  {initialValues?._id && (
                    <>
                      <Button
                        style={{ marginLeft: "auto", marginRight: 0, height: 24, fontSize: 11 }}
                        className="btn refresh"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={() => loadContent(contentId)}
                      >
                        Refresh
                      </Button>
                      <Button
                        className="btn add_new"
                        disabled={isSubmitting}
                        onClick={() => handleAddNewContent(formProps)}
                      >
                        <AddBox /> Add New
                      </Button>
                    </>
                  )}
                  {isLoading ? (
                    <LoadingWrapper message="Loading Content" />
                  ) : (
                    <>
                      <FormikSelectField
                        className="field selectfield content_type"
                        data-testid="content-type-select"
                        name="contentType"
                        label="Content Type"
                        options={initialValues?._id ? contentTypeOptions : contentTypeCreateOptions}
                        getOptionLabel={(option: IContentType) => option.title}
                        getOptionValue={(option: IContentType) => `${option._id}`}
                        onChange={(option: IContentType) => setContentType(option)}
                        closeMenuOnSelect={false}
                        isClearable={true}
                        disabled={!!initialValues?._id || isSubmitting} //cannot change the content type after creating it
                        menuPosition="fixed"
                      />

                      <FlightDropTarget formProps={formProps} />

                      <CustomFieldsV2 schema={schema} objectPath={customPath} displayHeader={true} />

                      <FileDrop
                        formProps={formProps}
                        setContentFile={setContentFile}
                        fileUploadRef={fileUploadRef}
                        requiresFile={formProps?.values?.contentType?.requiresFile}
                        maxFileSize={formProps?.values?.contentType?.maxFileSize}
                      />
                      {errors?.fileName && (
                        <span
                          key="filedrop_error"
                          className="error-message"
                        >{`File is required for ${contentType?.title}`}</span>
                      )}

                      {uploading && (
                        <Grid item xs={12} sx={{ p: 1 }}>
                          <Alert severity="warning" variant="outlined">
                            <AlertTitle>Upload in progress</AlertTitle>There are pending uploads in progress. Please do
                            not close the window.
                          </Alert>
                        </Grid>
                      )}
                      <Grid container className="buttons">
                        <Button
                          className="btn reset"
                          type="reset"
                          variant="contained"
                          color="secondary"
                          disabled={!dirty || isSubmitting}
                          onClick={handleResetContentForm}
                        >
                          Reset
                        </Button>
                        <Button
                          className="btn save"
                          data-testid="create-content-button"
                          variant="contained"
                          color="primary"
                          disabled={!contentTypeAssigned || isSubmitting || !dirty || !isValid}
                          type="submit"
                        >
                          {initialValues?._id ? "Update" : "Create"}
                        </Button>
                        {initialValues?._id && (
                          <Tooltip title={getDeleteTooltip()} placement="top">
                            <span>
                              <Button
                                className="btn save"
                                variant="contained"
                                color="primary"
                                disabled={
                                  isSubmitting ||
                                  values?.associatedFlights?.length > 0 ||
                                  values?.contentType?.allowDelete === false
                                }
                                onClick={() => handleDeleteContent(formProps)}
                              >
                                delete
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        {initialValues?._id && initialValues?.contentType?.type === ContentType.JourneyLogSheet && (
                          <Button
                            style={{ marginLeft: "auto", marginRight: 0 }}
                            className="btn review"
                            data-testid="review-logsheet-button"
                            variant="outlined"
                            disabled={isSubmitting || dirty}
                            onClick={() => openReviewContentWindow()}
                          >
                            review logsheet
                          </Button>
                        )}
                      </Grid>
                    </>
                  )}
                </Form>
              );
            }}
          </Formik>
          {content?._id && !isLoading && <Footer content={content} />}
        </Grid>
        <WarningModal
          displayModal={uploading}
          warningMessage={"An upload is still in progress"}
          setCloseWarning={setCloseWarning}
        />
      </Box>
    );
  } else {
    return <SpinnerProgress loading={true} message="Loading Content Types" />;
  }
};
